// Card 68, the inbox pins (R1846, ui-map-interface.md §17.3.68,
// docs/design/inbox.md §8).
//
// The Impeccable comp C of the INBOX hangs each NEEDS YOU item off a time
// rail by a numbered circle, and stands the same number on the map at the
// item's cell. This piece is the map half: one pin per item with a cell,
// with the number its item takes on the rail. `ui/inbox/needs.ts` is the
// ONE rule both halves read, so a number on the rail is always the number
// of its pin.
//
// **WHEN.** The pins stand while the INBOX is the open panel and NEEDS YOU
// is not folded (`ChartContext.needsPins`, which the page reads from the
// overlay). A pin points at a message of the commander's own INBOX and is
// no decoration, so the piece draws under the setting `off` too and no
// Chart theme reaches its gate (`off = true`, R1294).
//
// **THE LIT PIN** (comp C's focal point). The NEEDS YOU item the mouse
// points at, the one that holds the keyboard focus, or the one pressed
// last while the INBOX stands lights its pin: a second ring and the full
// alpha, drawn over every other pin (`ChartContext.needsLit`).
//
// **COST.** A static piece: it packs on the INBOX change (`inbox`), on a
// new world, a reading and a face, and at the end of a siege window or a
// change of the lit pin, which `outgrown` finds by two compares. The gate
// asks the kept answer of the rule, one identity compare a frame (§10.3).
//
// **THE PRESS.** `commands.clickMap` asks the furniture LAST, after the
// hulls and the sites (R702), and `pick` answers with the pin's own id.
// `fast/main.ts` holds the other half: it opens the INBOX message.

import { cellCenter } from '../../../world';
import { needsBad, needsOf } from '../../../ui/inbox/needs';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece, PickHit, RebuildReason } from '../context';
import { CHART_INK } from './shared';

/** One pin as the piece draws it. */
export interface Pin {
  /** The INBOX message the pin opens. */
  msgId: number;
  /** The number of its item on the time rail. */
  num: number;
  /** The cell, in world px. */
  x: number;
  y: number;
  /** The centre of the head, `head_dy` over the cell, in world px. */
  hy: number;
  /** The ring takes the bad ink. */
  bad: boolean;
  /** The pin of the lit item: a second ring at the full alpha. */
  lit: boolean;
}

/** The pin id a press answers with, so `main.ts` reads one shape. */
export const PIN_PREFIX = 'inbox:';

/** The INBOX message id inside a pin id, or 0 when the id is not one. */
export function pinMessageId(id: string): number {
  if (!id.startsWith(PIN_PREFIX)) return 0;
  const n = Number(id.slice(PIN_PREFIX.length));
  return Number.isInteger(n) && n > 0 ? n : 0;
}

export class InboxPinsPiece implements Piece {
  readonly id = 'inbox_pins';
  readonly tier = 'screen';
  readonly pack = 'static';
  readonly film = 'hide';
  readonly rebuildOn: readonly RebuildReason[] = ['hello', 'inbox', 'reading', 'font'];

  /** The pins of the last pack, newest item first. The tests read them. */
  pins: Pin[] = [];
  /** The wall second at which the list of the last pack changes alone. */
  private nextEnd = Infinity;
  /** The pick tolerance of the last pack, in world px. */
  private pickPx = 0;
  /** The lit message of the last pack, or 0. */
  private litId = 0;

  constructor(private readonly nowSec: () => number = () => Date.now() / 1000) {}

  visible(ctx: ChartContext): boolean {
    return ctx.needsPins && needsOf(ctx.state.inbox, this.nowSec()).pins > 0;
  }

  rebuild(ctx: ChartContext): void {
    const f = ctx.cfg.pieces.inbox_pins.fig;
    const list = needsOf(ctx.state.inbox, this.nowSec());
    this.nextEnd = list.nextEnd;
    this.pickPx = f.pick_px;
    this.litId = ctx.needsLit;
    this.pins = [];
    for (const { msg, num } of list.items) {
      if (num === 0) continue;
      const [x, y] = cellCenter(msg.cell);
      this.pins.push({
        msgId: msg.id, num, x, y, hy: y - f.head_dy, bad: needsBad(msg),
        lit: msg.id === this.litId,
      });
    }
  }

  /** A siege window of the list ended, or another item is lit: the pins
   *  changed with no frame. Two compares (§10.3). */
  outgrown(ctx: ChartContext): boolean {
    return ctx.needsLit !== this.litId || this.nowSec() >= this.nextEnd;
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    // The oldest first, so the newest pin, number 1, stands over the rest
    // where two pins share water; the lit pin stands over all of them.
    let lit: Pin | null = null;
    for (let i = this.pins.length - 1; i >= 0; i--) {
      const p = this.pins[i];
      if (p.lit) { lit = p; continue; }
      emitPin(sinks, ctx, p);
    }
    if (lit) emitPin(sinks, ctx, lit);
  }

  /**
   * The pin under a world point, or null: the head or the cell, whichever
   * is nearer. Of two pins under one press the nearer one wins, and of two
   * at one distance the lower number, the newer item.
   */
  pick(wx: number, wy: number, tolPx: number): PickHit | null {
    const tol = Math.max(tolPx, this.pickPx);
    let best: PickHit | null = null;
    let bestD = Infinity;
    for (const p of this.pins) {
      const d = Math.min(Math.hypot(p.x - wx, p.y - wy), Math.hypot(p.x - wx, p.hy - wy));
      if (d <= tol && d < bestD) {
        best = { kind: 'mark', id: `${PIN_PREFIX}${p.msgId}`, x: p.x, y: p.hy };
        bestD = d;
      }
    }
    return best;
  }
}

/**
 * One pin: the dot on the cell, the stem, the head with its ring, and the
 * number. A lit pin rings at the full alpha and takes a second ring
 * `lit_gap` outside the head.
 */
function emitPin(sinks: ChartSinks, ctx: ChartContext, p: Pin): void {
  const t = ctx.cfg.pieces.inbox_pins;
  const f = t.fig;
  const ink = p.bad ? CHART_INK.bad : CHART_INK.accent;
  const alpha = p.lit ? 1 : p.bad ? f.alpha_bad : f.alpha_plain;
  const hy = p.hy;
  sinks.circles.disc(p.x, p.y, f.dot_r, ink, alpha);
  sinks.segments.line(p.x, p.y, p.x, hy + f.head_r, f.stem_width, ink, alpha * f.stem_alpha);
  sinks.circles.disc(p.x, hy, f.head_r, CHART_INK.deep, f.disc_alpha);
  sinks.circles.ring(p.x, hy, f.head_r, f.ring_width, ink, alpha);
  if (p.lit) sinks.circles.ring(p.x, hy, f.head_r + f.lit_gap, f.lit_width, ink, 1);
  // The number is laid on its line centre, which is the head's centre.
  ctx.glyphs.word(sinks.quads, String(p.num), p.x, hy, 'mono', t.text.num,
    CHART_INK.pale, f.num_alpha, { anchor: 'centre', pivot: { x: p.x, y: hy } });
}

/** The registry's entry. */
export const inboxPins = new InboxPinsPiece();
